import React, { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Loader2, Compass, Network, RotateCw } from "lucide-react";
import SearchAutocomplete from "@/components/search/SearchAutocomplete";
import { useGraphData } from "@/hooks/useGraphData";
import { LensToggle } from "@/components/explore/LensToggle";

const CHIPS = [
  "What is Advaita Vedānta?",
  "How do Sāṃkhya and Yoga relate?",
  "What is Anattā?",
  "Difference between Ātman and Brahman",
];

const Mandala = () => (
  <svg
    className="pointer-events-none fixed inset-0 -z-10 m-auto w-[min(130vw,1100px)] h-[min(130vw,1100px)] opacity-[0.045] animate-spin-slow"
    viewBox="0 0 400 400"
    fill="none"
    aria-hidden="true"
  >
    {[190, 158, 126, 94, 62, 30].map((r, i) => (
      <circle key={r} cx="200" cy="200" r={r} stroke="#ffb020" strokeWidth={i % 2 ? 0.3 : 0.5} />
    ))}
    {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => (
      <ellipse
        key={deg}
        cx="200"
        cy="118"
        rx="11"
        ry="28"
        stroke="#ff7360"
        strokeWidth="0.5"
        transform={`rotate(${deg} 200 200)`}
      />
    ))}
  </svg>
);

export function DoorwayLanding({ onOpenNode }) {
  const navigate = useNavigate();
  const { nodes, edges, loading, error, refetch } = useGraphData();

  useEffect(() => {
    document.title = "Explore — Living Wisdom Explorer";
  }, []);

  const systems = useMemo(() => {
    const degree = {};
    edges.forEach((e) => {
      degree[e.source] = (degree[e.source] || 0) + 1;
      degree[e.target] = (degree[e.target] || 0) + 1;
    });
    return nodes
      .filter((n) => n.type === "knowledge_system")
      .map((n) => ({ ...n, connections: degree[n.id] || 0 }))
      .sort((a, b) => b.connections - a.connections || a.label.localeCompare(b.label));
  }, [nodes, edges]);

  return (
    <div className="relative flex-1 overflow-y-auto" data-testid="doorway-landing">
      <Mandala />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-16">
        {/* Search hero */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-3xl mx-auto text-center mb-14 lg:mb-20"
        >
          <div className="flex justify-center mb-6">
            <LensToggle />
          </div>
          <div className="text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] text-[#0a7d5f] mb-4">
            Enter through a tradition
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif tracking-tight text-[#14140f] leading-[1.05]">
            Ask anything, or wander
            <br />
            <em className="text-[#c23c28]">the great traditions</em>
          </h1>
          <p className="text-base sm:text-lg text-[#4e6073] mt-5 max-w-xl mx-auto leading-relaxed">
            Begin with a question, or step through a doorway below to trace how schools, concepts,
            texts and thinkers connect.
          </p>

          <div className="mt-9 max-w-xl mx-auto">
            <SearchAutocomplete
              testIdPrefix="doorway-search"
              placeholder="What is the nature of Ātman?"
              icon={<Sparkles className="w-5 h-5 text-[#a86400] shrink-0" />}
              submitIcon={<ArrowRight className="w-4 h-4" />}
              pillClassName="flex items-center gap-2 bg-white rounded-2xl shadow-sm border border-[#14140f]/20 pl-4 pr-2 py-3 focus-within:border-[#10a37f] focus-within:ring-1 focus-within:ring-[#10a37f] transition-colors"
              buttonClassName="shrink-0 w-10 h-10 rounded-xl bg-[#ff7360] text-[#14140f] flex items-center justify-center hover:bg-[#e85a46] transition-colors"
            />
            <div className="flex flex-wrap gap-2 justify-center mt-4">
              {CHIPS.map((text) => (
                <button
                  key={text}
                  onClick={() => navigate(`/answers?q=${encodeURIComponent(text)}`)}
                  data-testid="doorway-chip"
                  className="inline-flex items-center px-3 py-1 bg-white/60 border border-[#14140f]/10 rounded-full text-sm text-[#4e6073] hover:bg-white hover:text-[#14140f] transition-colors"
                >
                  {text}
                </button>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Gateway grid */}
        <div className="flex items-end justify-between mb-6 lg:mb-8 gap-4">
          <div>
            <div className="flex items-center gap-2 text-[#0a7d5f] mb-1">
              <Compass className="w-4 h-4" strokeWidth={1.75} />
              <span className="text-xs sm:text-sm font-semibold uppercase tracking-[0.2em]">
                Knowledge Systems
              </span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-serif tracking-tight text-[#14140f]">
              Choose a doorway
            </h2>
          </div>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-24 text-[#14140f]/40">
            <Loader2 className="w-7 h-7 animate-spin" />
          </div>
        )}
        {error && !loading && (
          <div className="py-16 text-center">
            <div className="text-[#b34040] text-sm mb-4">{error}</div>
            <button
              onClick={refetch}
              data-testid="doorway-retry-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#ff7360] text-[#14140f] text-sm font-medium hover:bg-[#e85a46] transition-colors"
            >
              <RotateCw className="w-4 h-4" /> Try again
            </button>
          </div>
        )}

        {!loading && !error && (
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8"
            initial="hidden"
            animate="show"
            variants={{ show: { transition: { staggerChildren: 0.05 } } }}
          >
            {systems.map((ks) => (
              <motion.button
                key={ks.id}
                variants={{ hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0 } }}
                onClick={() => onOpenNode(ks.id)}
                data-testid={`doorway-card-${ks.id}`}
                className="group text-left bg-white border border-[#14140f]/10 rounded-xl p-6 sm:p-8 cursor-pointer shadow-sm hover:shadow-xl hover:-translate-y-1 transition-[transform,box-shadow] duration-300 flex flex-col gap-3 focus:outline-none focus:ring-2 focus:ring-[#10a37f]"
              >
                <div className="flex items-center justify-between">
                  <span className="inline-flex w-9 h-9 items-center justify-center rounded-full bg-[#ff7360]/10 text-[#c23c28]">
                    <Network className="w-4 h-4" strokeWidth={1.75} />
                  </span>
                  <span className="text-sm text-[#4e6073]">{ks.connections} connections</span>
                </div>
                <h3 className="text-xl sm:text-2xl font-serif text-[#14140f] leading-tight">
                  {ks.label}
                </h3>
                {ks.description && (
                  <p className="text-sm text-[#4e6073] leading-relaxed line-clamp-3">
                    {ks.description}
                  </p>
                )}
                <div className="mt-auto pt-2 text-sm font-medium text-[#c23c28] flex items-center gap-1 group-hover:gap-2 transition-all">
                  Explore connections <ArrowRight className="w-4 h-4" />
                </div>
              </motion.button>
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
}
